import { FunctionComponent, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DashboardWrapper, Card, Text, Button } from 'src/shared';
import StatusBadge from 'src/shared/statusBadge';
import { Colors } from 'src/utils/color';
import { Size } from 'src/shared/text';
import { getPatients, getAnalysisJobs } from 'src/services/api';
import type { AnalysisJob, DisplayStatus } from 'src/types/job';
import { toDisplayStatus } from 'src/types/job';
import { stripRedundantPatientPrefix } from 'src/utils/patientLabel';

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------
const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 24px;
`;

const BackBtn = styled.button`
    background: none;
    border: 1px solid ${Colors.extra.light_gray};
    border-radius: 8px;
    padding: 8px 14px;
    cursor: pointer;
    font-size: 13px;
    color: ${Colors.extra.dark_gray};
    align-self: flex-start;
    &:hover { background: ${Colors.extra.light_gray}; }
`;

const HeaderCard = styled(Card)`
    padding: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
`;

const SubTitle = styled(Text)`
    color: ${Colors.extra.dark_gray};
`;

const MetricsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 12px;
`;

const MetricCard = styled(Card)`
    padding: 16px;
`;

const MetricValue = styled.div<{ $color?: string }>`
    font-size: 24px;
    font-weight: 700;
    color: ${p => p.$color || Colors.extra.black};
    margin-bottom: 2px;
`;

const MetricLabel = styled.div`
    font-size: 12px;
    color: ${Colors.extra.dark_gray};
`;

const SectionCard = styled(Card)`
    padding: 20px;
`;

const SectionTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${Colors.extra.black};
    margin-bottom: 16px;
`;

const Timeline = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
`;

const TimelineItem = styled.div<{ $selected?: boolean }>`
    display: grid;
    grid-template-columns: 200px 1fr 140px 140px;
    align-items: center;
    padding: 14px 16px;
    border-left: 3px solid ${p => p.$selected ? Colors.theme.primary : Colors.extra.light_gray};
    background: ${p => p.$selected ? `${Colors.theme.primary}06` : 'transparent'};
    cursor: pointer;
    transition: all 0.1s ease;
    &:hover { background: ${Colors.extra.light_gray}33; }
`;

const TimelineDateGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const TimelineDateRow = styled.div`
    display: flex;
    flex-direction: column;
    line-height: 1.25;
`;

const TimelineDateLabel = styled.span`
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${Colors.extra.dark_gray};
`;

const TimelineDateValue = styled.span`
    font-size: 13px;
    color: ${Colors.extra.black};
`;

const TimelineDateMuted = styled.span`
    font-size: 12px;
    color: ${Colors.extra.dark_gray};
    font-style: italic;
`;

const TimelineInfo = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: ${Colors.extra.black};
`;

const TimelineSub = styled.div`
    font-size: 11px;
    color: ${Colors.extra.dark_gray};
    margin-top: 2px;
`;

const CompareGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 60px 1fr;
    gap: 0;
    align-items: start;
`;

const CompareColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const CompareHeader = styled.div`
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: ${Colors.extra.dark_gray};
    margin-bottom: 8px;
    text-align: center;
`;

const CompareRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 6px 12px;
    border-bottom: 1px solid ${Colors.extra.light_gray};
    &:last-child { border-bottom: none; }
`;

const CompareLabel = styled.span`
    font-size: 12px;
    color: ${Colors.extra.dark_gray};
`;

const CompareVal = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${Colors.extra.black};
`;

const DeltaCol = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding-top: 28px;
`;

const Delta = styled.span<{ $positive?: boolean }>`
    font-size: 11px;
    font-weight: 600;
    color: ${p => p.$positive ? '#059669' : '#DC2626'};
    padding: 6px 0;
    border-bottom: 1px solid transparent;
`;

const EmptyMsg = styled.div`
    padding: 32px;
    text-align: center;
    color: ${Colors.extra.dark_gray};
    font-size: 14px;
`;

const ViewResultBtn = styled.button`
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid ${Colors.theme.primary};
    background: ${Colors.theme.primary};
    color: #fff;
    &:hover { opacity: 0.9; }
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatDateTime = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const date = d.toLocaleDateString();
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${date} ${time}`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const PatientDetailPage: FunctionComponent = () => {
    const { patientId } = useParams<{ patientId: string }>();
    const navigate = useNavigate();
    const [patient, setPatient] = useState<any>(null);
    const [jobs, setJobs] = useState<AnalysisJob[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedIdx, setSelectedIdx] = useState(0);

    useEffect(() => {
        if (!patientId) return;
        (async () => {
            setLoading(true);
            try {
                const [patRes, jobsRes] = await Promise.all([
                    getPatients(1, 100),
                    getAnalysisJobs({ page: 1, page_size: 100, patient: patientId }),
                ]);
                const patients = patRes?.data?.patients ?? patRes?.patients ?? [];
                const found = patients.find((p: any) => String(p.id) === String(patientId));
                setPatient(found || { id: patientId, external_id: `patient-${patientId}`, display_name: `Patient ${patientId}` });

                const jobList: AnalysisJob[] = jobsRes?.results ?? jobsRes?.data ?? [];
                setJobs(jobList);
            } catch { /* */ }
            finally { setLoading(false); }
        })();
    }, [patientId]);

    if (loading) {
        return (
            <DashboardWrapper><Container><Text>Loading patient…</Text></Container></DashboardWrapper>
        );
    }

    const patientIsSession = patient?.is_session ?? false;
    const patientNameRaw =
        patient?.display_name ?? patient?.external_id ?? patient?.full_name ?? `Patient ${patientId}`;
    const patientName = stripRedundantPatientPrefix(patientNameRaw, patientIsSession);
    const succeededJobs = jobs.filter(j => j.status === 'SUCCEEDED');

    const uploadDates = jobs.map(j => new Date(j.created_at).getTime()).filter(t => !isNaN(t));
    const lastUpload = uploadDates.length > 0 ? new Date(Math.max(...uploadDates)) : null;
    const currentJob = succeededJobs[selectedIdx];
    const previousJob = succeededJobs[selectedIdx + 1] ?? null;

    const counts = {
        total: jobs.length,
        completed: jobs.filter(j => j.status === 'SUCCEEDED').length,
        failed: jobs.filter(j => j.status === 'FAILED').length,
        reviewed: jobs.filter(j => j.reviewed).length,
    };

    return (
        <DashboardWrapper>
            <Container>
                <BackBtn onClick={() => navigate('/patients')}>&larr; Back to Patients</BackBtn>

                <HeaderCard>
                    <div>
                        <Text fontSize={Size.Large} fontWeight="600" color={Colors.extra.black}>
                            {patientIsSession ? 'Session' : 'Subject'}: {patientName}
                        </Text>
                        <SubTitle fontSize={Size.XSmall}>
                            {patient?.last_visit ? `Scan time: ${new Date(patient.last_visit).toLocaleDateString()}` : ''}
                            {patient?.last_visit && lastUpload ? ' · ' : ''}
                            {lastUpload ? `Last upload: ${lastUpload.toLocaleDateString()}` : ''}
                        </SubTitle>
                    </div>
                </HeaderCard>

                <MetricsRow>
                    <MetricCard>
                        <MetricValue>{counts.total}</MetricValue>
                        <MetricLabel>Total Scans</MetricLabel>
                    </MetricCard>
                    <MetricCard>
                        <MetricValue $color="#059669">{counts.completed}</MetricValue>
                        <MetricLabel>Completed</MetricLabel>
                    </MetricCard>
                    <MetricCard>
                        <MetricValue $color="#DC2626">{counts.failed}</MetricValue>
                        <MetricLabel>Failed</MetricLabel>
                    </MetricCard>
                    <MetricCard>
                        <MetricValue $color="#2563EB">{counts.reviewed}</MetricValue>
                        <MetricLabel>Reviewed</MetricLabel>
                    </MetricCard>
                </MetricsRow>

                {/* Scan history timeline */}
                <SectionCard>
                    <SectionTitle>Scan History</SectionTitle>
                    {jobs.length === 0 ? (
                        <EmptyMsg>No scans found for this patient.</EmptyMsg>
                    ) : (
                        <Timeline>
                            {jobs.map((job) => {
                                const display: DisplayStatus = toDisplayStatus(job);
                                const scanName = job.input_data_uri?.split('/').pop() ?? 'Unknown';
                                const isSucceeded = job.status === 'SUCCEEDED';
                                const succeededIndex = succeededJobs.indexOf(job);
                                const scanWhen = formatDateTime(job.scan_time);
                                const uploadWhen = formatDateTime(job.created_at);
                                return (
                                    <TimelineItem
                                        key={job.id}
                                        $selected={isSucceeded && succeededIndex === selectedIdx}
                                        onClick={() => { if (isSucceeded) setSelectedIdx(succeededIndex); }}
                                    >
                                        <TimelineDateGroup>
                                            <TimelineDateRow>
                                                <TimelineDateLabel>Scan</TimelineDateLabel>
                                                {scanWhen
                                                    ? <TimelineDateValue>{scanWhen}</TimelineDateValue>
                                                    : <TimelineDateMuted>Unknown</TimelineDateMuted>}
                                            </TimelineDateRow>
                                            <TimelineDateRow>
                                                <TimelineDateLabel>Upload</TimelineDateLabel>
                                                <TimelineDateValue>{uploadWhen}</TimelineDateValue>
                                            </TimelineDateRow>
                                        </TimelineDateGroup>
                                        <div>
                                            <TimelineInfo>{scanName}</TimelineInfo>
                                            <TimelineSub>
                                                {job.reviewed ? `Reviewed ${job.reviewed_at ? new Date(job.reviewed_at).toLocaleDateString() : ''}` : ''}
                                                {job.notes ? ` · ${job.notes.slice(0, 50)}${job.notes.length > 50 ? '…' : ''}` : ''}
                                            </TimelineSub>
                                        </div>
                                        <div><StatusBadge status={display} /></div>
                                        <div>
                                            {isSucceeded && (
                                                <ViewResultBtn onClick={e => { e.stopPropagation(); navigate(`/scans/${job.id}/result`); }}>
                                                    View Result
                                                </ViewResultBtn>
                                            )}
                                        </div>
                                    </TimelineItem>
                                );
                            })}
                        </Timeline>
                    )}
                </SectionCard>

                {/* Current vs Previous comparison */}
                {currentJob && (
                    <SectionCard>
                        <SectionTitle>
                            Metric Comparison
                            {previousJob ? ' (Current vs Previous)' : ' (Latest Scan)'}
                        </SectionTitle>
                        <CompareGrid>
                            <CompareColumn>
                                <CompareHeader>
                                    Current ({formatDateTime(currentJob.scan_time) || formatDateTime(currentJob.created_at)})
                                </CompareHeader>
                                <CompareRow>
                                    <CompareLabel>Status</CompareLabel>
                                    <StatusBadge status={toDisplayStatus(currentJob)} />
                                </CompareRow>
                                <CompareRow>
                                    <CompareLabel>Pipeline</CompareLabel>
                                    <CompareVal>{currentJob.pipeline_version}</CompareVal>
                                </CompareRow>
                                {currentJob.notes && (
                                    <CompareRow>
                                        <CompareLabel>Notes</CompareLabel>
                                        <CompareVal>{currentJob.notes.slice(0, 60)}</CompareVal>
                                    </CompareRow>
                                )}
                            </CompareColumn>

                            <DeltaCol>
                                <CompareHeader>Delta</CompareHeader>
                                <Delta>—</Delta>
                                <Delta>—</Delta>
                                {currentJob.notes && <Delta>—</Delta>}
                            </DeltaCol>

                            <CompareColumn>
                                <CompareHeader>
                                    {previousJob
                                        ? `Previous (${formatDateTime(previousJob.scan_time) || formatDateTime(previousJob.created_at)})`
                                        : 'No previous scan'}
                                </CompareHeader>
                                {previousJob ? (
                                    <>
                                        <CompareRow>
                                            <CompareLabel>Status</CompareLabel>
                                            <StatusBadge status={toDisplayStatus(previousJob)} />
                                        </CompareRow>
                                        <CompareRow>
                                            <CompareLabel>Pipeline</CompareLabel>
                                            <CompareVal>{previousJob.pipeline_version}</CompareVal>
                                        </CompareRow>
                                        {previousJob.notes && (
                                            <CompareRow>
                                                <CompareLabel>Notes</CompareLabel>
                                                <CompareVal>{previousJob.notes.slice(0, 60)}</CompareVal>
                                            </CompareRow>
                                        )}
                                    </>
                                ) : (
                                    <EmptyMsg>Upload another scan to enable comparison.</EmptyMsg>
                                )}
                            </CompareColumn>
                        </CompareGrid>
                    </SectionCard>
                )}

                {succeededJobs.length === 0 && (
                    <EmptyMsg>
                        No completed scans yet. Upload a scan and wait for processing to see results here.
                    </EmptyMsg>
                )}
            </Container>
        </DashboardWrapper>
    );
};

export default PatientDetailPage;
